"use client";

import React, { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useSession } from "next-auth/react";
import { TelnyxRTC } from "@telnyx/webrtc";
import { fetchInterceptor } from "@/app/lib/fetchInterceptor";
import { WEBRTC_CONNECTION_STATUS, WEBRTC_DEFAULT_PROFILE, WEBRTC_CALL_STATES, WebRTCConnectionStatus, WebRTCCallState, PROVIDER_TYPES, WEBRTC_PAGE_COOKIE_KEY, WEBRTC_PROFILES, WebRTCProfile } from "@/app/lib/constants";
import { useUserContext } from "@/context/useUserContext";
import { isSpecificProviderType, getProfilesByRole } from "@/app/lib/utils";
import { PhoneNumberOption } from "@/app/types/phoneTypes";
import { getPagePreferencesFromCookie, updatePagePreferencesInCookie } from "@/app/lib/cookies";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PhonePagePreferences {
  selectedProfile?: string;
  selectedNumber?: string;
}

export interface PhoneTokenData {
  token: string;
  callerNumber: string;
  callerName: string;
}

export interface CallInfo {
  id: string;
  state: WebRTCCallState;
  direction: "inbound" | "outbound";
  remoteNumber?: string;
  remoteCallerName?: string;
  dialedNumber?: string; // The number user dialed (outbound) or caller number (inbound)
  isMuted: boolean;
  isOnHold: boolean;
}

interface PhoneContextType {
  client: TelnyxRTC | null;
  connectionStatus: WebRTCConnectionStatus;
  tokenData: PhoneTokenData | null;
  reconnect: () => void;
  
  // Call state
  activeCall: CallInfo | null;
  incomingCall: CallInfo | null;
  callDuration: number;
  
  // Call actions
  answerCall: () => void;
  rejectCall: () => void;
  hangupCall: () => void;
  muteCall: (muted: boolean) => void;
  holdCall: (held: boolean) => void;
  
  // Profile & Number Selection
  selectedProfile: WebRTCProfile;
  setSelectedProfile: (profile: WebRTCProfile) => void;
  selectedNumber: string | null;
  setSelectedNumber: (number: string | null) => void;
  availableNumbers: PhoneNumberOption[];
  numbersLoading: boolean;
  numbersError: string | null;
  availableProfiles: string[];
}

// ─── Context ──────────────────────────────────────────────────────────────────

const PhoneContext = createContext<PhoneContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function PhoneProvider({ children }: { children: React.ReactNode }) {
  const interceptedFetch = fetchInterceptor();
  const { data: session } = useSession();
  const { role, providerType } = useUserContext();

  // useRef holds the latest client for cleanup — avoids the closure-captures-null bug
  const clientRef = useRef<TelnyxRTC | null>(null);
  // Stable ref so reconnect() exposed via context always calls the latest connect fn
  const connectRef = useRef<() => void>(() => {});
  
  // useState exposes the client to context consumers and triggers re-renders
  const [client, setClient] = useState<TelnyxRTC | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<WebRTCConnectionStatus>(
    WEBRTC_CONNECTION_STATUS.INITIALIZING as WebRTCConnectionStatus
  );
  const [tokenData, setTokenData] = useState<PhoneTokenData | null>(null);

  // Call state
  const [activeCall, setActiveCall] = useState<CallInfo | null>(null);
  const [incomingCall, setIncomingCall] = useState<CallInfo | null>(null);
  const [callDuration, setCallDuration] = useState<number>(0);

  // Profile & Number Selection state
  // Initialize from cookie but validate against role — prevents flicker on first render
  const getInitialProfile = (profilesForRole: string[]): WebRTCProfile => {
    const savedPreferences = getPagePreferencesFromCookie<PhonePagePreferences>(WEBRTC_PAGE_COOKIE_KEY);
    const savedProfile = savedPreferences?.selectedProfile;
    // Only use cookie value if it is allowed for this role
    if (savedProfile && profilesForRole.includes(savedProfile)) {
      return savedProfile as WebRTCProfile;
    }
    // Fall back to first available profile for this role, or global default
    return (profilesForRole.length > 0 ? profilesForRole[0] : WEBRTC_DEFAULT_PROFILE) as WebRTCProfile;
  };
  // Calculate once synchronously so both states start correct on first render
  const initialProfiles = getProfilesByRole(role ? Number(role) : null);
  const [selectedProfile, setSelectedProfile] = useState<WebRTCProfile>(() => getInitialProfile(initialProfiles));
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [availableNumbers, setAvailableNumbers] = useState<PhoneNumberOption[]>([]);
  const [numbersLoading, setNumbersLoading] = useState(false);
  const [numbersError, setNumbersError] = useState<string | null>(null);
  const [availableProfiles, setAvailableProfiles] = useState<string[]>(initialProfiles);

  // Refs for call management
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const activeCallRef = useRef<any>(null); // Raw TelnyxRTC call object
  const incomingCallRef = useRef<any>(null); // Raw TelnyxRTC call object
  const notificationHandlerRef = useRef<((notification: any) => void) | null>(null);
  const reconnectAttemptsRef = useRef(0);

  // Reconnect limiting constants
  const MAX_RECONNECT_ATTEMPTS = 3;
  const RECONNECT_DELAY_BASE = 1000; // 1 second

  const playRingtone = useCallback(() => {
    ringtoneRef.current?.play().catch((err) => {
      console.error("[PhoneProvider] Failed to play ringtone:", err);
    });
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, []);

  const startDurationTimer = useCallback(() => {
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const fetchNumbersForProfile = async (profile: WebRTCProfile, savedNumber?: string) => {
    // Don't fetch numbers if session doesn't exist
    if (!session?.user?.id) {
      console.warn("[PhoneProvider] Session not available, skipping number fetch");
      return;
    }

    try {
      setNumbersLoading(true);
      setNumbersError(null);
      
      const response = await interceptedFetch(
        `${process.env.NEXT_PUBLIC_API_URL}/admin/phone/numbers?profile=${profile}`
      );
      
      if (!response || !response.ok) throw new Error("Failed to fetch numbers");
      
      const data = await response.json();
      const numbers = data.numbers || [];
      setAvailableNumbers(numbers);
      
      // Verify saved number still exists in the fetched list
      const savedNumberExists = savedNumber && numbers.some((num: PhoneNumberOption) => num.phoneNumber === savedNumber);
      
      if (numbers.length > 0) {
        if (savedNumberExists) {
          // Use saved number if it still exists
          setSelectedNumber(savedNumber);
        } else {
          // Auto-select first number if saved number doesn't exist
          setSelectedNumber(numbers[0].phoneNumber);
          updatePagePreferencesInCookie(WEBRTC_PAGE_COOKIE_KEY, {
            selectedProfile: profile,
            selectedNumber: numbers[0].phoneNumber
          });
        }
      } else {
        setSelectedNumber(null);
      }
    } catch (error) {
      console.error("[PhoneProvider] Error fetching numbers:", error);
      setNumbersError("Failed to load phone numbers");
      setAvailableNumbers([]);
      setSelectedNumber(null);
    } finally {
      setNumbersLoading(false);
    }
  };

  const answerCall = useCallback(() => {
    const currentCall = incomingCallRef.current;
    if (!currentCall) {
      console.warn("[PhoneProvider] Cannot answer: no incoming call");
      return;
    }
    try {
      currentCall.answer();
    } catch (err) {
      console.error("[PhoneProvider] Answer error:", err);
    }
  }, []);

  const rejectCall = useCallback(() => {
    const currentCall = incomingCallRef.current;
    if (!currentCall) {
      console.warn("[PhoneProvider] Cannot reject: no incoming call");
      return;
    }
    try {
      currentCall.hangup();
    } catch (err) {
      console.error("[PhoneProvider] Reject error:", err);
    }
    setIncomingCall(null);
  }, []);

  const hangupCall = useCallback(() => {
    const currentCall = activeCallRef.current;
    if (currentCall) {
      try {
        currentCall.hangup();
      } catch (err) {
        console.error("[PhoneProvider] Hangup error:", err);
      }
    }
    setActiveCall(null);
    activeCallRef.current = null;
    stopDurationTimer();
  }, [stopDurationTimer]);

  const muteCall = useCallback(
    (muted: boolean) => {
      const currentCall = activeCallRef.current;
      if (!currentCall) return;

      try {
        if (muted) {
          currentCall.muteAudio();
        } else {
          currentCall.unmuteAudio();
        }
        setActiveCall((prev) =>
          prev ? { ...prev, isMuted: muted } : null
        );
      } catch (err) {
        console.error("[PhoneProvider] Mute error:", err);
      }
    },
    []
  );

  const holdCall = useCallback(
    (held: boolean) => {
      const currentCall = activeCallRef.current;
      if (!currentCall) return;

      try {
        if (held) {
          currentCall.hold();
        } else {
          currentCall.unhold();
        }
        setActiveCall((prev) =>
          prev ? { ...prev, isOnHold: held } : null
        );
      } catch (err) {
        console.error("[PhoneProvider] Hold error:", err);
      }
    },
    []
  );

  // Initialize selected number from cookies (run once on mount)
  useEffect(() => {
    const savedPreferences = getPagePreferencesFromCookie<PhonePagePreferences>(WEBRTC_PAGE_COOKIE_KEY);
    if (savedPreferences?.selectedNumber) {
      setSelectedNumber(savedPreferences.selectedNumber);
    }
  }, []); // Empty dependency array - run only once on mount

  // Fetch numbers when profile changes or session becomes available
  useEffect(() => {
    fetchNumbersForProfile(selectedProfile);
  }, [selectedProfile, session?.user?.id]);

  // Persist profile and number selection to cookie
  useEffect(() => {
    if (selectedProfile && selectedNumber) {
      updatePagePreferencesInCookie(WEBRTC_PAGE_COOKIE_KEY, {
        selectedProfile,
        selectedNumber,
      });
    }
  }, [selectedProfile, selectedNumber]); // Re-run when profile or number changes

  // Calculate available profiles based on user's role
  useEffect(() => {
    const roleIdNum = role ? Number(role) : null;
    const profiles = getProfilesByRole(roleIdNum);
    setAvailableProfiles(profiles);
    
    // If current profile is not in available profiles, reset to first available or default
    if (profiles.length > 0 && !profiles.includes(selectedProfile)) {
      const newProfile = profiles[0] as WebRTCProfile;
      setSelectedProfile(newProfile);
      updatePagePreferencesInCookie(WEBRTC_PAGE_COOKIE_KEY, {
        selectedProfile: newProfile,
        selectedNumber: selectedNumber
      });
    } else if (profiles.length === 0) {
      // User has no access to any profiles, reset to default
      setSelectedProfile(WEBRTC_DEFAULT_PROFILE);
    }
  }, [role]);

  useEffect(() => {

    const connect = async () => {
      try {
        // Only attempt connection if user is authenticated
        if (!session?.user?.id) {
          console.warn("[PhoneProvider] User not authenticated, skipping phone connection");
          setConnectionStatus(WEBRTC_CONNECTION_STATUS.INITIALIZING as WebRTCConnectionStatus);
          return;
        }

        // Check if user is LinqOps Support Provider (validates both role range and provider type)
        const isLinqopsSupportProvider = isSpecificProviderType(
          role?.toString(),
          providerType,
          PROVIDER_TYPES.LINQOPS_SUPPORT
        );

        // If user is not LinqOps Support Provider, do not continue
        if (!isLinqopsSupportProvider) {
          console.warn("[PhoneProvider] User is not LinqOps Support Provider, skipping phone connection");
          setConnectionStatus(WEBRTC_CONNECTION_STATUS.INITIALIZING as WebRTCConnectionStatus);
          return;
        }

        // Check if we've exceeded max reconnect attempts
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          console.error("[PhoneProvider] Max reconnect attempts reached");
          setConnectionStatus(WEBRTC_CONNECTION_STATUS.ERROR as WebRTCConnectionStatus);
          return;
        }

        setConnectionStatus(WEBRTC_CONNECTION_STATUS.CONNECTING as WebRTCConnectionStatus);

        // Proactively check microphone permission before attempting connection
        try {
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          if (micPermission.state === 'denied') {
            setConnectionStatus(WEBRTC_CONNECTION_STATUS.MIC_DENIED as WebRTCConnectionStatus);
            console.warn("[PhoneProvider] Microphone permission denied");
            return;
          }
        } catch (err) {
          // Permissions API not supported or error checking - continue with connection
          console.warn("[PhoneProvider] Could not check microphone permission:", err);
        }

        const response = await interceptedFetch(
          `${process.env.NEXT_PUBLIC_API_URL}/admin/phone/token?profile=${selectedProfile}`
        );

        if (!response?.ok) {
          const data = response ? await response.json() : {};
          if (data?.error === "WEBRTC_NOT_CONFIGURED") {
            setConnectionStatus(WEBRTC_CONNECTION_STATUS.NOT_CONFIGURED as WebRTCConnectionStatus);
            return;
          }
          throw new Error(data?.error || "Failed to fetch token");
        }

        const data = await response.json();
        const { token, callerNumber, callerName } = data;

        // Store token data for use in page component
        setTokenData({
          token,
          callerNumber,
          callerName,
        });

        // Tear down previous client before creating a new one
        if (clientRef.current) {
          try { clientRef.current.disconnect(); } catch { /* ignore */ }
        }

        const newClient = new TelnyxRTC({ login_token: token });
        
        // Point remote audio to the <audio> element (use ref if available, fallback to ID)
        newClient.remoteElement = remoteAudioRef.current ?? "remoteAudio";

        newClient.on("telnyx.ready", () => {
          console.log("[PhoneProvider] TelnyxRTC ready");
          reconnectAttemptsRef.current = 0; // Reset counter on successful connection
          setConnectionStatus(WEBRTC_CONNECTION_STATUS.READY as WebRTCConnectionStatus);
        });

        newClient.on("telnyx.error", async (err: any) => {
          const code = err?.error?.code;
          console.error("[PhoneProvider] TelnyxRTC error:", code, err?.error?.message);

          // Token expired — reconnect with fresh token using the same connect fn
          // 45003: RECONNECTION_EXHAUSTED
          // 46001: LOGIN_FAILED
          // 46003: AUTHENTICATION_REQUIRED
          if ([45003, 46001, 46003].includes(code)) {
            reconnectAttemptsRef.current++;
            const delay = RECONNECT_DELAY_BASE * Math.pow(2, reconnectAttemptsRef.current - 1);
            console.log(`[PhoneProvider] Reconnect attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`);
            setTimeout(() => connect(), delay);
            return;
          }

          // Reset counter on non-reconnectable errors
          reconnectAttemptsRef.current = 0;

          // Mic denied
          if (code === 42001) {
            setConnectionStatus(WEBRTC_CONNECTION_STATUS.MIC_DENIED as WebRTCConnectionStatus);
            return;
          }

          // Device not found
          if (code === 42002) {
            setConnectionStatus(WEBRTC_CONNECTION_STATUS.ERROR as WebRTCConnectionStatus);
            return;
          }

          // Network offline
          if (code === 48001) {
            setConnectionStatus(WEBRTC_CONNECTION_STATUS.OFFLINE as WebRTCConnectionStatus);
            return;
          }

          setConnectionStatus(WEBRTC_CONNECTION_STATUS.ERROR as WebRTCConnectionStatus);
        });

        newClient.on("telnyx.socket.close", () => {
          console.warn("[PhoneProvider] TelnyxRTC socket closed");
          setConnectionStatus(WEBRTC_CONNECTION_STATUS.DISCONNECTED as WebRTCConnectionStatus);
        });

        newClient.connect();
        clientRef.current = newClient; // ref: always up-to-date, used by cleanup
        setClient(newClient);          // state: triggers re-render for consumers

        // Register notification handler on the client
        const handleNotification = (notification: any) => {
          if (notification.type !== "callUpdate" || !notification.call) return;

          const call = notification.call;

          // Handle recovered call (reconnect scenario)
          if (call.recoveredCallId) {
            setActiveCall((prev) =>
              prev ? { ...prev, id: call.id } : null
            );
          }

          // Handle recovering state
          if (call.state === "recovering") {
            setActiveCall((prev) =>
              prev ? { ...prev, state: WEBRTC_CALL_STATES.CONNECTING } : null
            );
            return;
          }

          switch (call.state) {
            case "trying":
            case "requesting":
              activeCallRef.current = call;
              setActiveCall({
                id: call.id,
                state: WEBRTC_CALL_STATES.CONNECTING,
                direction: call.direction,
                remoteNumber: call.options?.remoteCallerNumber,
                remoteCallerName: call.options?.remoteCallerName,
                dialedNumber: call.options?.destinationNumber,
                isMuted: false,
                isOnHold: false,
              });
              break;

            case "ringing":
            case "early":
              if (call.direction === "inbound") {
                incomingCallRef.current = call;
                setIncomingCall({
                  id: call.id,
                  state: WEBRTC_CALL_STATES.INCOMING_RINGING,
                  direction: "inbound",
                  remoteNumber: call.options?.remoteCallerNumber,
                  remoteCallerName: call.options?.remoteCallerName,
                  dialedNumber: call.options?.remoteCallerNumber,
                  isMuted: false,
                  isOnHold: false,
                });
                playRingtone();
              } else {
                activeCallRef.current = call;
                setActiveCall((prev) =>
                  prev ? { ...prev, state: WEBRTC_CALL_STATES.RINGING } : null
                );
              }
              break;

            case "active":
              activeCallRef.current = call;
              setActiveCall((prev) =>
                prev
                  ? { ...prev, state: WEBRTC_CALL_STATES.ACTIVE }
                  : {
                      id: call.id,
                      state: WEBRTC_CALL_STATES.ACTIVE,
                      direction: "inbound",
                      remoteNumber: call.options?.remoteCallerNumber,
                      remoteCallerName: call.options?.remoteCallerName,
                      dialedNumber: call.options?.remoteCallerNumber,
                      isMuted: false,
                      isOnHold: false,
                    }
              );
              setIncomingCall(null);
              incomingCallRef.current = null;
              stopRingtone();
              startDurationTimer();
              break;

            case "held":
              setActiveCall((prev) =>
                prev ? { ...prev, state: WEBRTC_CALL_STATES.HELD } : null
              );
              break;

            case "hangup":
            case "destroy":
              setActiveCall(null);
              setIncomingCall(null);
              activeCallRef.current = null;
              incomingCallRef.current = null;
              stopRingtone();
              stopDurationTimer();
              break;

            default:
              break;
          }
        };

        notificationHandlerRef.current = handleNotification;
        newClient.on("telnyx.notification", handleNotification);
      } catch (err) {
        reconnectAttemptsRef.current = 0; // Reset counter on initialization error
        console.error("[PhoneProvider] Initialization error:", err);
        setConnectionStatus(WEBRTC_CONNECTION_STATUS.ERROR as WebRTCConnectionStatus);
      }
    };

    connectRef.current = connect;
    connect();

    return () => {
      // clientRef.current always holds the latest client — never null like a state closure
      if (clientRef.current) {
        try {
          if (notificationHandlerRef.current) {
            clientRef.current.off("telnyx.notification", notificationHandlerRef.current);
          }
          clientRef.current.disconnect();
        } catch { /* ignore */ }
        clientRef.current = null;
      }
    };
  }, [session?.user?.id, role, providerType, selectedProfile]);

  // Pre-unlock ringtone on first user interaction
  // This grants the element permission to play without user gesture for incoming calls
  useEffect(() => {
    const unlock = () => {
      const el = ringtoneRef.current;
      if (!el) return;
      el.play().then(() => el.pause()).catch(() => {});
      window.removeEventListener("pointerdown", unlock);
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  const value = useMemo<PhoneContextType>(
    () => ({
      client,
      connectionStatus,
      tokenData,
      activeCall,
      incomingCall,
      callDuration,
      answerCall,
      rejectCall,
      hangupCall,
      muteCall,
      holdCall,
      reconnect: () => connectRef.current(),
      selectedProfile,
      setSelectedProfile,
      selectedNumber,
      setSelectedNumber,
      availableNumbers,
      numbersLoading,
      numbersError,
      availableProfiles,
    }),
    [
      client,
      connectionStatus,
      tokenData,
      activeCall,
      incomingCall,
      callDuration,
      answerCall,
      rejectCall,
      hangupCall,
      muteCall,
      holdCall,
      selectedProfile,
      selectedNumber,
      availableNumbers,
      numbersLoading,
      numbersError,
      availableProfiles,
    ]
  );

  return (
    <>
      {/* Hidden audio element for remote audio output — persists across navigation */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={remoteAudioRef} id="remoteAudio" autoPlay playsInline />
      
      {/* Ringtone audio element for inbound calls */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={ringtoneRef} src="/sounds/ringtone.mp3" loop preload="auto" />
      
      <PhoneContext.Provider value={value}>{children}</PhoneContext.Provider>
    </>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePhoneContext(): PhoneContextType {
  const ctx = useContext(PhoneContext);
  if (!ctx) throw new Error("usePhoneContext must be used within PhoneProvider");
  return ctx;
}
